import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Stack,
    TextField,
    Typography,
    Select,
    MenuItem,
    Switch,
} from "@mui/material";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import {useMutation, useQuery} from "@tanstack/react-query";
import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn} from "@lib/apis";
import {useEffect, useState} from "react";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import IconButton from "@mui/material/IconButton";
import {AddCircleOutline, Delete} from "@mui/icons-material";
import {useSnackbar} from "notistack";

interface PageData {
    status_code: number
    status_text: string

    blacklist: Blacklist[]
}

interface Blacklist {
    id: number
    name: string
    domain: string
    send_email: boolean
    risk: string
    is_active: boolean
}

export default function AdminBlacklists() {
    const {enqueueSnackbar} = useSnackbar();

    const [pageData, setPageData] = useState<PageData>();
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [actionsDisabled, setActionsDisabled] = useState(false);
    const [filteredRows, setFilteredRows] = useState<Blacklist[]>([]);
    const [searchTerm, setSearchTerm] = useState("");    
    const [openNewWordsDialog, setOpenNewWordsDialog] = useState(false);

    // Query for fetching spam words data.
    const pageDataQuery = useQuery({
        queryKey: ["blacklistPageData"],
        queryFn: () => authenticateAndFetchData("/admin/admin-get-blacklist/"),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
    })

    useEffect(() => {
        if (pageDataQuery.data) {
            const data = pageDataQuery.data.data;
            setPageData(data);
            setFilteredRows(data.blacklist);
        }
    }, [pageDataQuery.data]);

    useEffect(() => {
        if (pageData?.blacklist) {
        const filtered = pageData.blacklist.filter((row) =>
            row.domain.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredRows(filtered);
        }
    }, [searchTerm, pageData]);

    // Mutation for deleting one word.
    const deleteBlacklistMutation = useMutation({
        mutationKey: ["deleteSingleBlacklist"],
        mutationFn: (id: number) => authenticateAndPostData("/admin/admin-add-or-remove-blacklist/", {
            action: "remove",
            ids: [id],
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            pageDataQuery.refetch().then();
            enqueueSnackbar("Domain removed successfully", {
                variant: "success",
            });
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message, {
                variant: "error",
            });
        }
    });

    const sendEmailBlacklistMutation = useMutation({
        mutationKey: ["sendEmailBlacklist"],
        mutationFn: (data:{id: number, sendEmail: boolean}) => authenticateAndPostData("/admin/admin-send-email-blacklist/", {            
            id: data.id,
            send_email: data.sendEmail,
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            pageDataQuery.refetch().then();            
            enqueueSnackbar("Domain send email updated", {
                variant: "success",
            });
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message, {
                variant: "error",
            });
        }
    });

    const activeStatusBlacklistMutation = useMutation({
        mutationKey: ["activeStatusBlacklist"],
        mutationFn: (data:{id: number, isActive: boolean}) => authenticateAndPostData("/admin/admin-status-blacklist/", {            
            id: data.id,
            is_active: data.isActive,
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            pageDataQuery.refetch().then();            
            enqueueSnackbar("Domain active status updated", {
                variant: "success",
            });
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message, {
                variant: "error",
            });
        }
    });

    const riskBlacklistMutation = useMutation({
        mutationKey: ["riskBlacklist"],
        mutationFn: (data:{id: number, risk: string}) => authenticateAndPostData("/admin/admin-risk-blacklist/", {            
            id: data.id,
            risk: data.risk,
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            pageDataQuery.refetch().then();            
            enqueueSnackbar("Domain risk updated", {
                variant: "success",
            });
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message, {
                variant: "error",
            });
        }
    });

    // Mutation for deleting multiple words.
    const deleteSelectedBlacklistMutation = useMutation({
        mutationKey: ["deleteMultipleBlacklist"],
        mutationFn: (ids: number[]) => authenticateAndPostData("/admin/admin-add-or-remove-blacklist/", {
            action: "remove",
            ids: ids,
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            pageDataQuery.refetch().then();
            enqueueSnackbar("Domain removed successfully", {
                variant: "success",
            });
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message, {
                variant: "error",
            });
        }
    });

    const triggerBlacklistCheckMutation = useMutation({
        mutationKey: ["triggerBlacklistCheck"],
        mutationFn: () => authenticateAndPostData("/admin/trigger-blacklist-check/", {
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {            
            enqueueSnackbar("Blacklist Domain check is in progress...", {
                variant: "success",
            });
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message, {
                variant: "error",
            });
        }
    });

    // Enabled/disables actions.
    useEffect(() => {
        setActionsDisabled(
            (selectedRows.length === 0) || deleteBlacklistMutation.isPending || deleteSelectedBlacklistMutation.isPending
        )
    }, [selectedRows, deleteBlacklistMutation.isPending, deleteSelectedBlacklistMutation.isPending]);

    const handleToggleSendEmail = (id, sendEmail) => {
        sendEmailBlacklistMutation.mutate({id, sendEmail})
    }

    const handleRiskChange = (id, risk) => {        
        riskBlacklistMutation.mutate({ id, risk })
    }

    const handleActiveStatusChange = (id, isActive) => {        
        activeStatusBlacklistMutation.mutate({ id, isActive })
    }

    const columns: GridColDef<Blacklist>[] = [
        {
            field: "name",
            headerName: "Blacklist Name",
            flex: 1,
            minWidth: 200,
            hideable: false,
            editable: false,
            filterable: true,
            sortable: true,
        },
        {
            field: "domain",
            headerName: "Blacklist Domain",
            flex: 1,
            minWidth: 200,
            hideable: false,
            editable: false,
            filterable: true,
            sortable: true,
        },
        {
            field: "send_email",
            headerName: "Send Email",
            minWidth: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
            <Switch
                checked={params.row.send_email}
                onChange={() => handleToggleSendEmail(params.row.id, !params.row.send_email)}
                color="primary"
            />
            ),
        },
        {
            field: "is_active",
            headerName: "Active",
            minWidth: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
            <Switch
                checked={params.row.is_active}
                onChange={() => handleActiveStatusChange(params.row.id, !params.row.is_active)}
                color="primary"
            />
            ),
        },
        {
            field: "risk",
            headerName: "Risk Level",
            minWidth: 180,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
            <Select
                size="small"
                value={params.row.risk}
                onChange={(e) => handleRiskChange(params.row.id, e.target.value)}
                fullWidth
            >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="moderate">Moderate</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="very_high">Very High</MenuItem>
                <MenuItem value="severe">Severe</MenuItem>
            </Select>
            ),
        },
        {
            field: "delete",
            headerName: "Delete",
            filterable: false,
            sortable: false,
            hideable: false,
            editable: false,
            renderCell: (params) => {
                return (
                    <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteSingle(params.row.id)}
                    >
                        <Delete/>
                    </IconButton>
                )
            }
        }
    ];

    function handleBlacklistCheck(){
        triggerBlacklistCheckMutation.mutate()
    }

    function handleDeleteSingle(id: number) {
        deleteBlacklistMutation.mutate(id);
    }

    function handleDeleteSelected(ids: number[]) {
        deleteSelectedBlacklistMutation.mutate(ids);
    }

    // ============================================================
    // --------------------- MAIN RENDER CODE ---------------------
    // ============================================================

    if (pageDataQuery.isLoading) {
        return (
            <PageLoading/>
        )

    } else if (pageDataQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed}/>

    } else if (pageData) {
        return (
            <Stack direction={"column"} spacing={2} sx={{pb: 4}}>
                <Typography fontWeight={"bold"} variant={"h5"}>Manage Blacklist domains</Typography>
                <Divider/>
                <Stack direction={"column"} spacing={2}>
                    {/* Table Actions */}
                    <Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
                        <Stack direction={"row"} spacing={2}>
                            <Button variant={"contained"}
                                            size={"small"}                                            
                                            disabled={false}
                                            onClick={() => handleBlacklistCheck()}>
                                Blacklist check
                            </Button>
                            <Button variant={"contained"}
                                            size={"small"}
                                            startIcon={<AddCircleOutline/>}
                                            disabled={false}
                                            onClick={() => setOpenNewWordsDialog(true)}>
                                Add New Blacklist Domains
                            </Button>
                            <Button color={"error"}
                                            variant={"contained"}
                                            size={"small"}
                                            startIcon={<Delete/>}
                                            disabled={actionsDisabled}
                                            onClick={() => {
                                                handleDeleteSelected(selectedRows);
                                            }}>
                                Delete Selected
                            </Button>
                        </Stack>
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search domains..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Box>
                    {/* Table */}
                    <Box sx={{width: '100%'}}>
                        <DataGrid
                            rows={filteredRows}
                            columns={columns}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10,
                                    },
                                },
                            }}
                            pageSizeOptions={[10, 50, 100]}
                            checkboxSelection
                            disableRowSelectionOnClick
                            onRowSelectionModelChange={rowSelectionModel => {
                                setSelectedRows(rowSelectionModel as number[])
                            }}
                        />
                    </Box>
                </Stack>

                <AddBlacklistDialog open={openNewWordsDialog}
                                                     onClose={() => setOpenNewWordsDialog(false)}
                                                     onSuccess={() => {
                                                         setOpenNewWordsDialog(false);
                                                         pageDataQuery.refetch().then();
                                                     }}/>
            </Stack>
        )
    } else {
        // Ideally it should not reach here.
        return <></>
    }
}

function AddBlacklistDialog(props: {
    open: boolean,
    onClose: () => void,
    onSuccess: () => void,
}) {
    const {enqueueSnackbar} = useSnackbar();

    const [blacklistText, setBlacklistText] = useState<string>('');

    // Mutation to add spam words.
    const addBlacklistMutation = useMutation({
        mutationKey: ["addBlacklist"],
        mutationFn: (blacklists: { domain: string; name: string }[]) => authenticateAndPostData("/admin/admin-add-or-remove-blacklist/", {
            action: "add",
            domains: blacklists
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: (response) => {
            setBlacklistText("");
            props.onSuccess();
            let duplicate_domains: string[] = response.data["duplicate_domains"];
			let error: string = response.data["error"];
            if (duplicate_domains) {
                    enqueueSnackbar(error, {
                        variant: "error",
                    });
            }else{
            enqueueSnackbar("Domain added successfully", {
                variant: "success",
            });
        }
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message, {
                variant: "error",
            });
        }
    });

    function handleSubmit() {
        let blacklists: { domain: string; name: string }[] = [];
        
        blacklistText.trim().split("\n").forEach(line => {
            const [domain, name] = line.split("|").map(x => x.trim());
            if (domain && name) {
                blacklists.push({ domain, name });
            }
        });

        if (blacklists.length > 0) {
            addBlacklistMutation.mutate(blacklists);
        }
    }

    // ============================================================
    // --------------------- MAIN RENDER CODE ---------------------
    // ============================================================

    return (
        <Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add Blacklist Domains</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{mb: 2}}>
                    Enter new blacklist domains, one per line eg. example.com | Example Blacklist:
                </DialogContentText>
                <TextField
                    autoFocus
                    multiline
                    rows={8}
                    fullWidth
                    variant="outlined"
                    placeholder="Enter one per line: domain | name"
                    value={blacklistText}
                    onChange={(e) => setBlacklistText(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}
                                color={"primary"}
                                disabled={addBlacklistMutation.isPending}>
                    Cancel
                </Button>
                <Button color={"primary"}
                                variant={"contained"}
                                onClick={handleSubmit}
                                disabled={addBlacklistMutation.isPending || !blacklistText.trim()}>
                    Add Blacklist Domains
                </Button>
            </DialogActions>
        </Dialog>
    );
}
